// src/pages/RoyaltyPage.tsx
// ロイヤリティ管理ページ

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CurrencyYenIcon } from '@heroicons/react/24/outline';
import RoyaltyDashboard from '../components/royalty/RoyaltyDashboard';
import { getUserCharacters } from '../services/characterService';

const RoyaltyPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hasLicensingEnabled, setHasLicensingEnabled] = useState(false);
  const [isCheckingLicensing, setIsCheckingLicensing] = useState(true);

  // ユーザーがライセンスを有効にしたキャラクターを持っているかチェック
  useEffect(() => {
    const checkLicensing = async () => {
      if (!user) {
        setIsCheckingLicensing(false);
        return;
      }

      try {
        const characters = await getUserCharacters(user.uid);
        const hasLicensing = characters.some(
          (char) => char.licensing?.enabled === true
        );
        setHasLicensingEnabled(hasLicensing);
      } catch (error) {
        console.error('Failed to check licensing status:', error);
      } finally {
        setIsCheckingLicensing(false);
      }
    };

    checkLicensing();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">ログインが必要です</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-purple-600 text-white px-6 py-2 rounded-full hover:bg-purple-700 transition-colors"
          >
            ログインへ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f7f8] dark:bg-gray-900">
      {/* Page Title and Content */}
      <div className="max-w-6xl mx-auto px-4 pt-6 pb-6">
        <div className="flex items-center gap-2 mb-6">
          <CurrencyYenIcon className="w-6 h-6 text-purple-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">ロイヤリティ</h1>
        </div>
        {isCheckingLicensing ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <RoyaltyDashboard
            hasLicensingEnabled={hasLicensingEnabled}
            alwaysShow={true}
          />
        )}

        {/* Info Section */}
        <div className="mt-8 p-4 bg-white dark:bg-white/5 rounded-2xl border border-gray-200 dark:border-white/10">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            ロイヤリティについて
          </h3>
          <div className="space-y-2 text-xs text-gray-600 dark:text-white/60">
            <p>
              • キャラクターのライセンスを有効にすると、他のユーザーがあなたのキャラクターでグッズを作成できます
            </p>
            <p>
              • グッズが販売されると、設定したロイヤリティ率（1〜20%）が自動的にあなたに支払われます
            </p>
            <p>
              • ロイヤリティの受け取りには収益化の承認とStripe Connect登録が必要です
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoyaltyPage;
